import type { QueryClient } from '@tanstack/react-query';
import { questionsSpaceKey } from '@lib/cache/questions';
import { invalidateSpaceMembershipQueries } from '@utils/invalidateSpaceMembershipQueries';
import {
  communitiesKey,
  communityDetailKey,
  spaceMembersKey,
  spacesMyJoinedBaseKey,
} from './queryKeys';

/**
 * Whose membership moved, from the perspective of the client doing the invalidating.
 *
 * `'self'` means the VIEWER's own row changed, so content visibility changed with it: the
 * backend's space-scoped question and event reads gate on the viewer's `SpaceMember` status
 * (`_apply_private_event_filter`), and the cross-space my-spaces / My Calendars aggregations
 * gain or lose this space's rows.
 *
 * `'peer'` means somebody ELSE's row changed. The roster and the denormalized `member_count`
 * move for every approved member, but no content becomes visible or invisible to the viewer,
 * so the content families are deliberately not swept. (See the peer-vs-self tiering rationale
 * on the `SPACE_MEMBER_*` case in `useRealtimeInvalidation`.)
 */
export type MembershipChangeScope = 'self' | 'peer';

/**
 * Refreshes the caches a space-membership transition moves, for the two pipelines that learn
 * about a transition WITHOUT having caused it:
 *
 *  1. `useRealtimeInvalidation` -- the `space.member_*` SSE echo.
 *  2. `useCommunityBySlugOrId` -- the pending-membership safety net observing the transition
 *     on a poll, when the SSE datagram was dropped (TECH-386).
 *
 * The `'self'` tier is `invalidateSpaceMembershipQueries` (src/utils/), the single definition of
 * which caches a change to the VIEWER's membership moves (the viewer's own held posts among
 * them), plus `questionsSpaceKey(spaceId)`, which the space feed needs freshened after a
 * membership change (several causing callers sweep it themselves too).
 *
 * The `'peer'` tier is NOT a viewer-membership change, so it stays local: someone else's row moved
 * the roster and the denormalized `member_count`, and nothing the viewer may see.
 *
 * Factory keys only -- never raw arrays -- so a key-shape change reaches every pipeline at once.
 *
 * @param spaceId which space moved. Optional because the SSE payload can arrive truncated; the
 *   global surfaces still fire, and only the per-space keys are skipped.
 */
export function invalidateMembershipQueries(
  queryClient: QueryClient,
  { spaceId, scope }: { spaceId?: string; scope: MembershipChangeScope },
): void {
  if (scope === 'self') {
    // An observed transition of the viewer's own row: membership provably moved. A truncated
    // payload names no space, which is "cannot tell which", not "nothing" -- the global surfaces
    // still fire and only the per-space keys are skipped.
    invalidateSpaceMembershipQueries(queryClient, {
      change: spaceId ? { kind: 'these', ids: [spaceId] } : { kind: 'unknown', candidateIds: [] },
      alreadySweptByCaller: {
        eventRoot: false,
        spaceEventLists: false,
        mySpacesFeed: false,
        heldPostPages: false,
      },
    });
    if (spaceId) void queryClient.invalidateQueries({ queryKey: questionsSpaceKey(spaceId) });
    return;
  }

  // Roster + the denormalized `member_count` every cached CommunityRead carries.
  if (spaceId) {
    void queryClient.invalidateQueries({ queryKey: spaceMembersKey(spaceId) });
  }
  void queryClient.invalidateQueries({ queryKey: communityDetailKey() });
  void queryClient.invalidateQueries({ queryKey: communitiesKey() });
  void queryClient.invalidateQueries({ queryKey: spacesMyJoinedBaseKey() });
}
